<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRegisterEntryMiniCard',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    noIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.noIcon,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noIcon');
    }
  );

  const act_3601807637515735_showModal_nyj2gw7an_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RegisterEntry',
      props: {
        oid: props.item.id,
      },
    };
  };

  const act_3601807637515735_showModal_nyj2gw7an =
    new actionTypes.ShowModalAction({
      actionArgs: act_3601807637515735_showModal_nyj2gw7an_getActionArgs,
    });
  const inst_evh_3601807637515735_clickPreventStop_7ypmrjx71 =
    new actionTypes.EventHandlerImpl({
      action: act_3601807637515735_showModal_nyj2gw7an,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_3601807637515735_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3601807637515735_clickPreventStop_7ypmrjx71],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_3601807637515735_clickPreventStop_7ypmrjx71,
    inst_evh_3601807637515735_clickPreventStop,
  };

  const evh_3601807637515735_clickPreventStop_7ypmrjx71 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3601807637515735_clickPreventStop_7ypmrjx71.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3601807637515735_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center">
    <ItpIcon
      :name="$config.register.icon"
      spacing="me-3"
      :title="pgettext('Register', 'Register')"
      class="me-2"
      v-if="!noIcon"
    >
    </ItpIcon>
    <ItpBox direction="column">
      <div class="me-2" v-if="item.version">
        {{ item.version.name }} &middot; {{ item.version.company_name }}
      </div>
      <ItpBox alignItems="center">
        <ItpText strong :text="item.entry_number" class="me-2"> </ItpText>
        <ItpLink
          :icon="$config.externalLinkIcon"
          @click.prevent.stop="
            evh_3601807637515735_clickPreventStop($event, {})
          "
        >
        </ItpLink>
      </ItpBox>
    </ItpBox>
  </ItpBox>
</template>
